import React, { useState, useRef, useEffect } from 'react';
import { ASSET_BASE_URL } from '../../../../config/constants';
import { getSavedWorkflows, deleteSavedWorkflow, saveWorkflowSnapshot } from './workflowStorage';
import { buildDefaultStepsWithSkills } from './workflowHelpers';
import { useAuth } from '../../../../hooks/useAuth';
import { signOut } from '../../../../lib/auth';
import { writeFile } from '../../../../utils/api';

const CURRENT_WORKFLOW_NAME_KEY = 'bonzai-current-workflow-name';
const CURRENT_WORKFLOW_ID_KEY = 'bonzai-current-workflow-id';

export default function WorkflowEditorHeader({
  showOnboarding,
  canShowWriteButton,
  hasUnsavedChanges,
  isSaving,
  currentWorkflowName,
  setCurrentWorkflowName,
  isEditingWorkflowName,
  setIsEditingWorkflowName,
  savedWorkflowsList,
  setSavedWorkflowsList,
  steps,
  setSteps,
  setWorkflowTitle,
  setSelectedStepId,
  savedSnapshotRef,
  savedClaudeMdRef,
  workflowSwitchHintClicked,
  setWorkflowSwitchHintClicked,
  onWriteToRepo,
  onOpenSignInModal
}) {
  const [showWorkflowsMenu, setShowWorkflowsMenu] = useState(false);
  const [showCheckpointSaved, setShowCheckpointSaved] = useState(false);
  const [showWriteSuccess, setShowWriteSuccess] = useState(false);
  const [hoveredWorkflowId, setHoveredWorkflowId] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const workflowsMenuRef = useRef(null);
  const userMenuRef = useRef(null);
  const currentWorkflowId = localStorage.getItem(CURRENT_WORKFLOW_ID_KEY);
  const autosaveTimeoutRef = useRef(null);
  const prevIsSavingRef = useRef(isSaving);
  const { user, loading: authLoading, profile } = useAuth();

  // Test write function
  const handleTestWrite = async () => {
    try {
      console.log('[TEST] Attempting to write findme.md...');
      const testContent = '# Find Me!\n\nThis is a test file to verify write functionality.\n\nTimestamp: ' + new Date().toISOString();
      const result = await writeFile('findme.md', testContent);
      console.log('[TEST] Write successful! Result:', result);
      alert('Test file written successfully! Check findme.md in your project root.');
    } catch (err) {
      console.error('[TEST] Write failed:', err);
      alert('Test write failed: ' + err.message);
    }
  };

  // Load saved workflow on mount
  useEffect(() => {
    const savedWorkflowId = localStorage.getItem(CURRENT_WORKFLOW_ID_KEY);
    if (savedWorkflowId) {
      const workflows = getSavedWorkflows();
      const savedWorkflow = workflows.find(w => w.id === savedWorkflowId);
      if (savedWorkflow && savedWorkflow.steps) {
        setSteps(savedWorkflow.steps);
        savedSnapshotRef.current = JSON.stringify(savedWorkflow.steps);
      }
    }
  }, []); // Only run on mount

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (workflowsMenuRef.current && !workflowsMenuRef.current.contains(e.target)) {
        setShowWorkflowsMenu(false);
      }
    }
    if (showWorkflowsMenu) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showWorkflowsMenu]);

  // Close user menu on outside click
  useEffect(() => {
    function handleClick(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    }
    if (showUserMenu) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showUserMenu]);

  // Detect when writing completes and show success message
  useEffect(() => {
    console.log('[WriteSuccess] useEffect triggered. prevIsSaving:', prevIsSavingRef.current, 'isSaving:', isSaving);
    if (prevIsSavingRef.current && !isSaving) {
      // Saving just completed
      console.log('[WriteSuccess] Setting showWriteSuccess to true');
      setShowWriteSuccess(true);
      setTimeout(() => {
        console.log('[WriteSuccess] Hiding success message');
        setShowWriteSuccess(false);
      }, 3000);
    }
    prevIsSavingRef.current = isSaving;
  }, [isSaving]);

  // Autosave when name or steps change (debounced)
  useEffect(() => {
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }

    autosaveTimeoutRef.current = setTimeout(() => {
      if (currentWorkflowName && steps && steps.length > 0) {
        const workflowId = localStorage.getItem(CURRENT_WORKFLOW_ID_KEY);
        const newId = saveWorkflowSnapshot(currentWorkflowName, steps, new Date().toISOString(), workflowId);

        // If this was a new workflow (no ID), store the new ID
        if (!workflowId) {
          localStorage.setItem(CURRENT_WORKFLOW_ID_KEY, newId);
        }

        // Update snapshot ref to reflect saved state
        savedSnapshotRef.current = JSON.stringify(steps);

        // Refresh the saved workflows list
        setSavedWorkflowsList(getSavedWorkflows());
      }
    }, 1000); // Debounce by 1 second

    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [currentWorkflowName, steps, savedSnapshotRef, setSavedWorkflowsList]);

  const handleNewWorkflow = () => {
    const emptySteps = buildDefaultStepsWithSkills([]);
    setSteps(emptySteps);
    setCurrentWorkflowName('Untitled Workflow');
    localStorage.setItem(CURRENT_WORKFLOW_NAME_KEY, 'Untitled Workflow');
    localStorage.removeItem(CURRENT_WORKFLOW_ID_KEY);
    setWorkflowTitle('Select Workflow');
    setSelectedStepId(null);
    savedSnapshotRef.current = JSON.stringify(emptySteps);
    savedClaudeMdRef.current = '';
  };

  const handleSwitchWorkflow = (workflow) => {
    setSteps(workflow.steps);
    setCurrentWorkflowName(workflow.name);
    localStorage.setItem(CURRENT_WORKFLOW_NAME_KEY, workflow.name);
    localStorage.setItem(CURRENT_WORKFLOW_ID_KEY, workflow.id);
    setWorkflowTitle(workflow.name);
    savedSnapshotRef.current = JSON.stringify(workflow.steps);
    setShowWorkflowsMenu(false);
  };

  const handleDeleteWorkflow = (e, workflowId) => {
    e.preventDefault();
    e.stopPropagation();

    deleteSavedWorkflow(workflowId);
    setSavedWorkflowsList(getSavedWorkflows());

    // If deleting the current workflow, create a new one
    if (workflowId === currentWorkflowId) {
      handleNewWorkflow();
    }
  };

  return (
    <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 20px',
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 20
      }}>
      {/* Left: Logo */}
      <div
        onClick={() => window.location.href = '/'}
        style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: '0 0 auto' }}
      >
        <img
          src={`${ASSET_BASE_URL}/favicon.jpg`}
          alt="Bonzai"
          style={{ width: '28px', height: '28px', borderRadius: '6px' }}
        />
        <span style={{ fontSize: '16px', fontWeight: 600, color: '#111827', letterSpacing: '-0.01em' }}>bonzai</span>
      </div>

      {/* Middle: Workflow Name (rename only) */}
      <div style={{
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          transition: 'border-color 0.15s ease'
        }} ref={workflowsMenuRef}>
          {/* Workflow name - editable */}
          {isEditingWorkflowName ? (
            <input
              type="text"
              value={currentWorkflowName}
              onChange={(e) => setCurrentWorkflowName(e.target.value)}
              onBlur={() => {
                localStorage.setItem(CURRENT_WORKFLOW_NAME_KEY, currentWorkflowName);
                setIsEditingWorkflowName(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  localStorage.setItem(CURRENT_WORKFLOW_NAME_KEY, currentWorkflowName);
                  setIsEditingWorkflowName(false);
                } else if (e.key === 'Escape') {
                  setCurrentWorkflowName(localStorage.getItem(CURRENT_WORKFLOW_NAME_KEY) || 'Untitled Workflow');
                  setIsEditingWorkflowName(false);
                }
              }}
              autoFocus
              style={{
                fontSize: '15px',
                fontWeight: 600,
                color: '#111827',
                background: 'transparent',
                border: 'none',
                padding: '6px 12px',
                outline: 'none',
                minWidth: '200px',
                textAlign: 'center'
              }}
            />
          ) : (
            <div
              onClick={() => setIsEditingWorkflowName(true)}
              style={{
                fontSize: '15px',
                fontWeight: 600,
                color: '#111827',
                cursor: 'text',
                padding: '6px 12px',
                minWidth: '200px',
                textAlign: 'center'
              }}
              title="Click to rename"
            >
              {currentWorkflowName}
            </div>
          )}
        </div>
      </div>

      {/* Right: Write to Repo + Auth */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '0 0 auto' }}>
  

        {/* Write to Repo */}
        {canShowWriteButton && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
            {showWriteSuccess && (
              <span style={{
                fontSize: '13px',
                color: '#6b7280',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                  <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd" />
                </svg>
                Written
              </span>
            )}
            <button
              onClick={onWriteToRepo}
              style={{
                padding: '8px 16px',
                background: '#f97316',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 500,
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#ea580c';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f97316';
              }}
              title="Write skills to repository"
            >
                {isSaving ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" style={{ animation: 'spin 1s linear infinite' }}>
                    <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="20 12" />
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '14px', height: '14px' }}>
                    <path d="M10 2a.75.75 0 0 1 .75.75v5.59l1.95-2.1a.75.75 0 1 1 1.1 1.02l-3.25 3.5a.75.75 0 0 1-1.1 0L6.2 7.26a.75.75 0 1 1 1.1-1.02l1.95 2.1V2.75A.75.75 0 0 1 10 2Z" />
                    <path d="M5.273 4.5a1.25 1.25 0 0 0-1.205.918l-1.523 5.52c-.006.02-.01.041-.015.062H6a1 1 0 0 1 .894.553l.448.894a1 1 0 0 0 .894.553h3.438a1 1 0 0 0 .86-.49l.606-1.02A1 1 0 0 1 14 11h3.47a1.318 1.318 0 0 0-.015-.062l-1.523-5.52a1.25 1.25 0 0 0-1.205-.918h-1.477a.75.75 0 0 1 0-1.5h1.477a2.75 2.75 0 0 1 2.652 2.02l1.523 5.52c.066.236.099.48.099.726V15a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-3.234c0-.246.033-.49.099-.726l1.523-5.52A2.75 2.75 0 0 1 5.273 3.5h1.477a.75.75 0 0 1 0 1.5H5.273Z" />
                  </svg>
                )}
                {isSaving ? 'Writing...' : 'Write to Repo'}
            </button>
          </div>
        )}

        {/* Divider */}
        {canShowWriteButton && !authLoading && (
          <div style={{
            width: '1px',
            height: '32px',
            background: '#e5e7eb'
          }} />
        )}

        {/* Auth Button/Profile */}
        {!authLoading && (
          user ? (
            <div style={{ position: 'relative' }} ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                style={{
                  padding: '4px',
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fff';
                }}
                title="Account menu"
              >
                <img
                  src={profile?.avatar_url || user.user_metadata?.avatar_url}
                  alt={profile?.username || user.email}
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%'
                  }}
                />
              </button>

              {/* User Dropdown Menu */}
              {showUserMenu && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  right: 0,
                  minWidth: '200px',
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  zIndex: 1000,
                  overflow: 'hidden'
                }}>
                  {/* User info */}
                  <div style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #f3f4f6'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#111827',
                      marginBottom: '2px'
                    }}>
                      {profile?.username || user.email}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#9ca3af'
                    }}>
                      {user.email}
                    </div>
                  </div>

                  {/* Sign out button */}
                  <button
                    onClick={() => {
                      signOut();
                      setShowUserMenu(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#374151',
                      cursor: 'pointer',
                      transition: 'background 0.15s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f9fafb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                      <path fillRule="evenodd" d="M3 4.25A2.25 2.25 0 0 1 5.25 2h5.5A2.25 2.25 0 0 1 13 4.25v2a.75.75 0 0 1-1.5 0v-2a.75.75 0 0 0-.75-.75h-5.5a.75.75 0 0 0-.75.75v11.5c0 .414.336.75.75.75h5.5a.75.75 0 0 0 .75-.75v-2a.75.75 0 0 1 1.5 0v2A2.25 2.25 0 0 1 10.75 18h-5.5A2.25 2.25 0 0 1 3 15.75V4.25Z" clipRule="evenodd" />
                      <path fillRule="evenodd" d="M6 10a.75.75 0 0 1 .75-.75h9.546l-1.048-.943a.75.75 0 1 1 1.004-1.114l2.5 2.25a.75.75 0 0 1 0 1.114l-2.5 2.25a.75.75 0 1 1-1.004-1.114l1.048-.943H6.75A.75.75 0 0 1 6 10Z" clipRule="evenodd" />
                    </svg>
                    Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => {
                console.log('[DEBUG] Sign in button clicked, onOpenSignInModal:', onOpenSignInModal);
                if (onOpenSignInModal) {
                  onOpenSignInModal();
                } else {
                  console.error('[ERROR] onOpenSignInModal is not defined');
                }
              }}
              style={{
                padding: '8px 16px',
                background: '#000',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#1a1a1a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#000';
              }}
              title=""
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
              Sign in
            </button>
          )
        )}
      </div>
    </div>
  );
}
